import React, { KeyboardEvent } from "react";
import Row from "react-bootstrap/Row";
import Col from "react-bootstrap/Col";
import Form from "react-bootstrap/Form";

import { MultipleSensorChartType } from "constant";
import { omit } from "lodash";

import deleteSvg from "assets/svg/delete.svg";

import { MultipleChartBlockProps, MultipleChartColumnData } from "types/Charts";
import { MultipleChartColumn } from "./MultipleChartColumn";
import Avatar from "@mui/material/Avatar";
import {
    getWidgetConfigByWidgetType,
    WidgetType,
} from "components/dashboard/utils/widgetConfig";

export const MultipleChartBlock: React.FC<MultipleChartBlockProps> = React.memo(
    (props) => {
        const {
            blockKey,
            onDelete,
            onChange,
            defaultValue,
            isSingleUpdate,
            isEdit,
            onSubmitMultipleChart,
        } = props;
        const [formValues, setFormValues] =
            React.useState<MultipleChartColumnData>({
                multipleChartType: "same",
                chart_name: "Multiple sensor chart",
                data: {},
            });
        const [eventTrigger, setEventTrigger] = React.useState<any>();

        React.useEffect(() => {
            defaultValue && setFormValues(defaultValue);
        }, [defaultValue]);

        React.useEffect(() => {
            eventTrigger && onChange(formValues);
            setEventTrigger(undefined);
            // eslint-disable-next-line react-hooks/exhaustive-deps
        }, [formValues]);

        const { minItems } = getWidgetConfigByWidgetType(
            WidgetType.SENSOR_CHART_MULTIPLE
        );
        const selectedSensorCount = Object.values(formValues.data || {}).reduce(
            (count, column) => count + Object.keys(column?.devices ?? {}).length,
            0
        );
        const showMinSensorsMessage = selectedSensorCount < (minItems ?? 1);

        return (
            <div className="multiple-chart-block rounded p-4">
                <Row>
                    <Col>
                        <Row>
                            <Col sm={12} className="mb-3">
                                <p>Widget Name</p>
                                <Form.Control
                                    type="text"
                                    className="multiple-chart-name"
                                    placeholder="Enter widget name"
                                    value={formValues.chart_name || ""}
                                    onChange={(event) => {
                                        setFormValues({
                                            ...formValues,
                                            chart_name: event.target.value,
                                        });
                                        setEventTrigger(event);
                                    }}
                                    onKeyPress={(
                                        e: KeyboardEvent<HTMLInputElement>
                                    ) => {
                                        if (e.key === "Enter") {
                                            e.preventDefault();
                                            if (onSubmitMultipleChart)
                                                onSubmitMultipleChart(e);
                                        }
                                    }}
                                />
                            </Col>
                            <Col sm={12}>
                                <p>Select sensor type</p>
                                <Row>
                                    {Object.keys(MultipleSensorChartType).map(
                                        (key) => (
                                            <Col sm={12} key={key} md={6}>
                                                <Form.Check
                                                    className={`multiple-chart-block__select-radio mb-1 mb-lg-0 ${formValues.multipleChartType ===
                                                        key &&
                                                        "multiple-chart-block__select-radio--active"
                                                        }`}
                                                    checked={
                                                        formValues.multipleChartType ===
                                                        key
                                                    }
                                                    type="radio"
                                                    label={
                                                        MultipleSensorChartType[
                                                        key as keyof typeof MultipleSensorChartType
                                                        ]
                                                    }
                                                    name={`multiple-sensor-type-${blockKey.key}`}
                                                    id={`multiple-sensor-type-${key}-${blockKey.key}`}
                                                    value={key}
                                                    custom
                                                    onChange={(event) => {
                                                        setFormValues(
                                                            (prev: any) => ({
                                                                ...prev,
                                                                multipleChartType: key,
                                                                data: key === "same"
                                                                    ? omit(prev.data, "1")
                                                                    : prev.data,
                                                            })
                                                        );
                                                        setEventTrigger(event);
                                                    }}
                                                />
                                            </Col>
                                        )
                                    )}
                                </Row>
                                <hr className="my-4 w-100" />
                            </Col>

                            <Col sm={12}>
                                <Row>
                                    {Array.from(
                                        new Array(
                                            formValues.multipleChartType ===
                                                "same"
                                                ? 1
                                                : 2 // Set number columns
                                        ).keys()
                                    ).map((column) => (
                                        <MultipleChartColumn
                                            key={
                                                formValues.multipleChartType +
                                                column
                                            }
                                            isEdit={isEdit}
                                            column={column}
                                            data={formValues}
                                            onChange={(data) => {
                                                setFormValues((prev: any) => ({
                                                    ...prev,
                                                    data: {
                                                        ...prev.data,
                                                        [column]: data,
                                                    },
                                                }));
                                                setEventTrigger("change");
                                            }}
                                        />
                                    ))}
                                </Row>
                            </Col>

                            {showMinSensorsMessage && (
                                <Col sm={12} className="mt-3 text-danger">
                                    A minimum of two sensors must be selected.
                                </Col>
                            )}
                        </Row>
                    </Col>
                    <Col
                        sm={1}
                        className="delete-block d-flex justify-content-end"
                    >
                        {!isSingleUpdate && (
                            <Avatar
                                alt="delete"
                                src={deleteSvg}
                                onClick={() => onDelete?.(blockKey)}
                                sx={{
                                    width: 24,
                                    height: 24,
                                    cursor: "pointer",
                                }}
                            />
                        )}
                    </Col>
                </Row>
            </div>
        );
    }
);
